import { useEffect, useState } from 'react';
import { Download, X, Loader2, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { checkForUpdate, applyUpdate, type UpdateInfo } from '../lib/tauri';

/**
 * Auto-updater banner — only shown inside the Tauri shell. checkForUpdate()
 * is a no-op in the browser, so this component renders nothing on the
 * Vercel deploy. Inside Tauri it pings the configured endpoint on mount,
 * shows a slim emerald banner if an update is available, and offers to
 * download + relaunch.
 *
 * Dismissals are session-only — next launch checks again. We don't want to
 * "remember dismissed" across sessions because the user may have just
 * deferred for the day, not forever.
 */
export function UpdateBanner() {
  const [update, setUpdate] = useState<UpdateInfo | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState<{ downloaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    checkForUpdate()
      .then((u) => { if (!cancelled) setUpdate(u); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  if (!update || dismissed) return null;

  const onInstall = async () => {
    setInstalling(true);
    setError(null);
    try {
      await applyUpdate((d, t) => setProgress({ downloaded: d, total: t }));
      setSuccess(true);
      // Tauri will relaunch automatically; if it doesn't, leave the success state
    } catch (e: any) {
      setError(e?.message || String(e));
      setInstalling(false);
    }
  };

  const pct = progress && progress.total > 0
    ? Math.round((progress.downloaded / progress.total) * 100)
    : 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="fixed top-0 left-0 right-0 z-40"
        style={{
          background: 'linear-gradient(90deg, rgba(16,185,129,0.18) 0%, rgba(6,78,59,0.4) 100%)',
          borderBottom: '1px solid rgba(16,185,129,0.4)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center gap-4">
          <div className="flex items-center gap-2 flex-none">
            {success ? (
              <CheckCircle2 size={16} className="heartbeat" style={{ color: '#34D399' }} />
            ) : installing ? (
              <Loader2 size={16} className="animate-spin" style={{ color: '#34D399' }} />
            ) : (
              <Download size={16} className="heartbeat" style={{ color: '#34D399' }} />
            )}
            <span className="text-sm font-medium" style={{ color: '#F0FDF4' }}>
              {success && 'Update installed — restarting…'}
              {!success && installing && progress && (
                <>Downloading {pct}% <span className="font-mono opacity-60">({formatBytes(progress.downloaded)} / {formatBytes(progress.total)})</span></>
              )}
              {!success && !installing && (
                <>Update available: <span className="font-mono" style={{ color: '#34D399' }}>v{update.version}</span> (you have v{update.currentVersion})</>
              )}
              {error && <span style={{ color: '#FCA5A5' }}>{error}</span>}
            </span>
          </div>
          <div className="flex-1" />
          {!installing && !success && (
            <>
              <button
                onClick={onInstall}
                className="text-xs font-semibold px-3 py-1 rounded transition-all"
                style={{
                  background: '#10B981',
                  color: '#050505',
                }}
              >
                Install + restart
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="opacity-60 hover:opacity-100 transition-opacity"
                title="Dismiss"
              >
                <X size={14} style={{ color: '#F0FDF4' }} />
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function formatBytes(n: number): string {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
